import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Upload, FileText, Trash2, Loader2, Search, Eye, BookOpen, RefreshCw, ClipboardList } from 'lucide-react';
import { SiteFilter } from '@/components/admin/SiteFilter';

interface CompanyDocument {
  id: string;
  document_name: string;
  document_type: string;
  category: string | null;
  file_url: string;
  summary: string | null;
  extracted_text: string | null;
  keywords: string[];
  is_active: boolean;
  created_at: string;
  site_id: string | null;
}

const documentTypes = [
  { value: 'sop', label: 'SOP (Standard Operating Procedure)' },
  { value: 'jsa', label: 'JSA (Job Safety Analysis)' },
  { value: 'work_instruction', label: 'Work Instruction' },
  { value: 'policy', label: 'Policy' },
  { value: 'training', label: 'Training Material' },
  { value: 'inspection_checklist', label: 'Inspection Checklist' },
  { value: 'other', label: 'Other' },
];

const categories = [
  'general', 'ppe', 'welding', 'electrical', 'chemical', 
  'fire_safety', 'lifting', 'confined_space', 'heights', 'machinery'
];

interface AdminDocumentsProps {
  siteId?: string | null;
}

const AdminDocuments = ({ siteId: propSiteId }: AdminDocumentsProps = {}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<CompanyDocument[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<CompanyDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<string>(propSiteId || 'all');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<CompanyDocument | null>(null);
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);
  const [parsingChecklistId, setParsingChecklistId] = useState<string | null>(null);
  const [adminProfile, setAdminProfile] = useState<{ user_id: string; company_id: string | null; site_id: string | null } | null>(null);

  // Use prop siteId if provided
  useEffect(() => {
    if (propSiteId) {
      setSelectedSiteFilter(propSiteId);
    }
  }, [propSiteId]);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    documentName: '',
    documentType: 'sop',
    category: 'general',
    file: null as File | null,
  });

  // Fetch admin profile on mount
  useEffect(() => {
    const fetchAdminProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('worker_profiles')
          .select('user_id, company_id, site_id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (profile) {
          setAdminProfile(profile);
        } else {
          setAdminProfile({ user_id: user.id, company_id: '', site_id: null });
        }
      }
    };
    fetchAdminProfile();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...documents];

    if (searchTerm) {
      filtered = filtered.filter(doc => 
        doc.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doc.category?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(doc => doc.document_type === filterType);
    }

    if (selectedSiteFilter !== 'all') {
      filtered = filtered.filter(doc => doc.site_id === selectedSiteFilter);
    }

    setFilteredDocuments(filtered);
  }, [documents, searchTerm, filterType, selectedSiteFilter]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('company_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadForm(prev => ({ 
        ...prev, 
        file,
        documentName: prev.documentName || file.name.replace(/\.[^/.]+$/, '')
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.documentName) {
      toast({
        title: 'Error',
        description: 'Please provide a file and document name',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      // Upload file to storage
      const fileExt = uploadForm.file.name.split('.').pop();
      const fileName = `${Date.now()}_${uploadForm.documentName.replace(/\s+/g, '_')}.${fileExt}`;

      console.log('Uploading file:', fileName);
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('company-documents')
        .upload(fileName, uploadForm.file);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        toast({
          title: 'Upload Error',
          description: `Storage error: ${uploadError.message}`,
          variant: 'destructive',
        });
        return;
      }

      console.log('File uploaded successfully:', uploadData);
      const { data: { publicUrl } } = supabase.storage
        .from('company-documents')
        .getPublicUrl(fileName);

      console.log('Public URL:', publicUrl);

      // Insert document record with company_id and uploaded_by
      const { data: docData, error: dbError } = await supabase
        .from('company_documents')
        .insert({
          document_name: uploadForm.documentName,
          document_type: uploadForm.documentType,
          category: uploadForm.category,
          file_url: publicUrl,
          is_active: true,
          company_id: adminProfile?.company_id || null,
          uploaded_by: adminProfile?.user_id || null,
        })
        .select()
        .single();

      if (dbError) {
        console.error('Database insert error:', dbError);
        toast({
          title: 'Database Error',
          description: `Failed to save document record: ${dbError.message}`,
          variant: 'destructive',
        });
        return;
      }

      console.log('Document record created:', docData);

      // Process document with edge function (extract text)
      if (docData) {
        try {
          console.log('Invoking process-document function...');
          const { data: processData, error: processError } = await supabase.functions.invoke('process-document', {
            body: { documentId: docData.id, fileUrl: publicUrl, fileName }
          });
          
          if (processError) {
            console.error('Document processing error:', processError);
            toast({
              title: 'Processing Notice',
              description: 'Document uploaded but text extraction may be pending.',
            });
          } else {
            console.log('Document processed:', processData);
          }
          
          // PHASE 6: Auto-trigger inspection checklist parsing
          // Only proceed if process-document succeeded (text was extracted)
          if (uploadForm.documentType === 'inspection_checklist' && !processError) {
            console.log('Triggering inspection checklist parsing...');
            const { data: parseData, error: parseError } = await supabase.functions.invoke('parse-inspection-checklist', {
              body: { 
                documentId: docData.id,
                companyId: adminProfile?.company_id || '00000000-0000-0000-0000-000000000001',
                siteId: selectedSiteFilter !== 'all' ? selectedSiteFilter : null
              }
            });
            
            if (parseError) {
              console.error('Checklist parsing error:', parseError);
              toast({
                title: 'Checklist Parsing',
                description: 'Checklist uploaded. Parsing may take a moment.',
              });
            } else {
              console.log('Checklist parsed:', parseData);
              toast({
                title: 'Checklist Parsed',
                description: `Created ${parseData?.requirementsCount || 0} atomic requirements.`,
              });
            }
          }
        } catch (processError) {
          console.error('Document processing exception:', processError);
        }
      }

      toast({
        title: 'Success',
        description: 'Document uploaded successfully',
      });

      setShowUploadDialog(false);
      setUploadForm({
        documentName: '',
        documentType: 'sop',
        category: 'general',
        file: null,
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload document',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const { error } = await supabase
        .from('company_documents')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Document deleted successfully',
      });
      fetchDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive',
      });
    }
  };

  const handleReprocess = async (doc: CompanyDocument) => {
    setReprocessingId(doc.id);
    try {
      const { error } = await supabase.functions.invoke('process-document', {
        body: { 
          documentId: doc.id, 
          fileUrl: doc.file_url,
          fileName: doc.document_name 
        }
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Document reprocessed successfully',
      });
      fetchDocuments();
    } catch (error) {
      console.error('Reprocess error:', error);
      toast({
        title: 'Error',
        description: 'Failed to reprocess document',
        variant: 'destructive',
      });
    } finally {
      setReprocessingId(null);
    }
  };

  const handleParseChecklist = async (doc: CompanyDocument) => {
    setParsingChecklistId(doc.id);
    try {
      const { data, error } = await supabase.functions.invoke('parse-inspection-checklist', {
        body: { 
          documentId: doc.id,
          companyId: adminProfile?.company_id || null,
          siteId: doc.site_id || (selectedSiteFilter !== 'all' ? selectedSiteFilter : null)
        }
      });

      if (error) throw error;

      toast({
        title: 'Checklist Parsed',
        description: `Created ${data?.requirementsCount || 0} atomic requirements for inspection.`,
      });
    } catch (error) {
      console.error('Parse checklist error:', error);
      toast({
        title: 'Error',
        description: 'Failed to parse checklist. Check if document has extracted text.',
        variant: 'destructive',
      });
    } finally {
      setParsingChecklistId(null);
    }
  };

  // Remove old filter logic since we now use useEffect
  // const filteredDocuments is now a state variable

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      sop: 'bg-blue-500/10 text-blue-500',
      jsa: 'bg-orange-500/10 text-orange-500',
      work_instruction: 'bg-green-500/10 text-green-500',
      policy: 'bg-purple-500/10 text-purple-500',
      training: 'bg-pink-500/10 text-pink-500',
      other: 'bg-gray-500/10 text-gray-500',
    };
    return colors[type] || colors.other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Knowledge Base Documents
          </h2>
          <p className="text-sm text-muted-foreground">
            Upload SOPs, JSAs, and Work Instructions for AI-powered assistance
          </p>
        </div>
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Upload className="h-4 w-4" />
              Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
              <DialogDescription>
                Upload a document to the knowledge base for AI retrieval
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Document Name</Label>
                <Input
                  value={uploadForm.documentName}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, documentName: e.target.value }))}
                  placeholder="e.g., SOP Welding Safety"
                />
              </div>
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select
                  value={uploadForm.documentType}
                  onValueChange={(value) => setUploadForm(prev => ({ ...prev, documentType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={uploadForm.category}
                  onValueChange={(value) => setUploadForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>
                        {cat.replace('_', ' ').charAt(0).toUpperCase() + cat.replace('_', ' ').slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>File (PDF, Word, etc.)</Label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileChange}
                />
              </div>
              <Button onClick={handleUpload} disabled={uploading} className="w-full">
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {!propSiteId && (
              <SiteFilter 
                value={selectedSiteFilter} 
                onChange={setSelectedSiteFilter}
                className="w-full sm:w-48"
              />
            )}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {documentTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Content</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No documents found</p>
                    <p className="text-sm">Upload your first document to get started</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocuments.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        {doc.document_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={getTypeColor(doc.document_type)}>
                        {doc.document_type.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="capitalize">{doc.category?.replace('_', ' ') || '-'}</span>
                    </TableCell>
                    <TableCell>
                      {doc.extracted_text ? (
                        <Badge variant="default" className="bg-green-500/10 text-green-500">
                          Processed
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-500">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {doc.document_type === 'inspection_checklist' && doc.extracted_text && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleParseChecklist(doc)}
                            disabled={parsingChecklistId === doc.id}
                            title="Parse as inspection checklist"
                            className="text-primary hover:text-primary"
                          >
                            <ClipboardList className={`h-4 w-4 ${parsingChecklistId === doc.id ? 'animate-pulse' : ''}`} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleReprocess(doc)}
                          disabled={reprocessingId === doc.id}
                          title="Reprocess document"
                        >
                          <RefreshCw className={`h-4 w-4 ${reprocessingId === doc.id ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(doc.file_url, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(doc.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{documents.length}</p>
            <p className="text-sm text-muted-foreground">Total Documents</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-500">
              {documents.filter(d => d.document_type === 'sop').length}
            </p>
            <p className="text-sm text-muted-foreground">SOPs</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-orange-500">
              {documents.filter(d => d.document_type === 'jsa').length}
            </p>
            <p className="text-sm text-muted-foreground">JSAs</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-500">
              {documents.filter(d => d.document_type === 'work_instruction').length}
            </p>
            <p className="text-sm text-muted-foreground">Work Instructions</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDocuments;
